import { Table, TableBody, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { StyledTableCell } from '../../../../../../../common/Styleguide/Common/StyledTable';
import { Submission } from '../../submission-graphql';
import moment from 'moment';
import { getBuildTooltip, getGameBuildStatusColor, getGameBuildStatusReadable } from '../../../../../../helpers/getGameBuildStatusFormat';
import { generateSubmissionPortalPreviewRoute } from '../../../QATool/helpers/preview-helpers';
import { StyledButton, StyledLoadingButton } from '../../../../../../../common/Styleguide/Common/Button';
import { formatGraphDateAndTime } from '../../../../../../../common/formatting';
import { GameBuild } from '../../../../Admin/Submission/graphql/game-build-graphql';
import { COLORS } from '../../../../../../../common/Styleguide/Common/colors';
import { deleteGameBuild } from '../../Mutations/delete-game-build-mutation';
import { useState } from 'react';
import { ApolloError } from '@apollo/client';
import { GraphQLError } from '../../../../../../../common/components/ApolloError/GraphQLError';
import ConfirmationDialog, { ConfirmationResult } from '../../../../../../../common/components/ConfirmationDialog/ConfirmationDialog';
import { canDeveloperDeleteGameBuild } from '@crazygames/game-types/src/utils/canDeveloperDeleteGameBuild';

interface SubmissionOverviewGameBuildsProps {
  submission: Submission;
}

const SubmissionOverviewGameBuilds: React.FC<SubmissionOverviewGameBuildsProps> = ({ submission }: SubmissionOverviewGameBuildsProps) => {
  const descendingBuilds = [...submission.gameBuilds].sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt)));

  const [apolloError, setApolloError] = useState<ApolloError | undefined>(undefined);
  const [deleting, setDeleting] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleDeleteGameBuild = async (id: string) => {
    setApolloError(undefined);
    setDeleting(true);

    try {
      await deleteGameBuild({ id }, submission.id);
    } catch (e) {
      setApolloError(e as ApolloError);
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmDelete = (result: ConfirmationResult) => {
    if (result === 'confirmed' && deleteId) {
      handleDeleteGameBuild(deleteId);
    }
    setDeleteId(null);
  };

  const getActionButtons = (gameBuild: GameBuild, index: number) => {
    // non DRAFT builds, that is builds that were already submitted and passsed QA, can be previewed in QA
    const buttons = [];

    if (gameBuild.status !== 'DRAFT') {
      buttons.push(
        <StyledButton
          key="preview"
          sx={{
            borderRadius: '8px',
            width: '100%',
          }}
          variant="contained"
          href={generateSubmissionPortalPreviewRoute(submission.id, {
            gameBuildId: gameBuild.id,
            qaTool: true,
            disableSubmitQA: true,
          })}
          target="_blank"
        >
          Preview
        </StyledButton>,
      );
    }

    // for the latest DRAFT build devs can go an pass QA
    // only for the latest, to avoid confusing untested cases of what happens if an older build becomes submitted
    // reminder that usually devs will be offered to pass QA right after saving game details, or submitting a new version
    if (gameBuild.status === 'DRAFT' && index === 0) {
      const label = gameBuild.loaderType === 'iframe' ? 'Complete QA and notify admins of changes' : 'Submit update';

      buttons.push(
        <StyledButton
          key="submit"
          sx={{
            borderRadius: '8px',
            width: '100%',
            bgcolor: 'green',
          }}
          variant="contained"
          href={generateSubmissionPortalPreviewRoute(submission.id, {
            gameBuildId: gameBuild.id,
            qaTool: true,
          })}
          target="_blank"
        >
          {label}
        </StyledButton>,
      );
    }

    if (canDeveloperDeleteGameBuild(gameBuild.status)) {
      buttons.push(
        <StyledLoadingButton
          key="delete"
          sx={{
            borderRadius: '8px',
            width: '100%',
          }}
          variant="contained"
          color="red"
          loading={deleting && deleteId === gameBuild.id}
          disabled={deleting}
          onClick={() => setDeleteId(gameBuild.id)}
        >
          Discard draft
        </StyledLoadingButton>,
      );
    }

    return buttons;
  };

  return (
    <>
      <TableContainer
        sx={{
          maxHeight: 300,
          '& .MuiTableHead-root .MuiTableCell-stickyHeader': {
            backgroundColor: COLORS.black[70],
          },
        }}
      >
        {apolloError && <GraphQLError error={apolloError} textColor={COLORS.alert[100]} />}
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ fontWeight: 'bold' }}>Creation date</StyledTableCell>
              <StyledTableCell sx={{ fontWeight: 'bold' }}>Build ID</StyledTableCell>
              <StyledTableCell sx={{ fontWeight: 'bold' }}>Actions</StyledTableCell>
              <StyledTableCell sx={{ fontWeight: 'bold' }}>Status</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {descendingBuilds.map((gameBuild, index) => (
              <TableRow key={gameBuild.id}>
                <StyledTableCell>{formatGraphDateAndTime(gameBuild.createdAt)}</StyledTableCell>
                <StyledTableCell>{gameBuild.id}</StyledTableCell>
                <StyledTableCell sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {getActionButtons(gameBuild, index)}
                </StyledTableCell>
                <StyledTableCell sx={{ fontWeight: 'bold', color: getGameBuildStatusColor(gameBuild.status) }}>
                  <Tooltip title={getBuildTooltip(gameBuild.status)} placement="top">
                    <div>{getGameBuildStatusReadable(gameBuild.status)}</div>
                  </Tooltip>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {deleteId && (
        <ConfirmationDialog title="Discard Draft" onClose={handleConfirmDelete} cancelable={true} dangerous={true} okButtonText="Discard">
          Are you sure you want to discard this draft build? This action cannot be undone.
        </ConfirmationDialog>
      )}
    </>
  );
};

export default SubmissionOverviewGameBuilds;
